/**
 * Global Toast Deduplication Service
 * 
 * Prevents duplicate toasts across different event sources:
 * - Position events (real-time position monitoring)
 * - Trade events (exchange polling & comprehensive snapshots)
 * - Handles race conditions during SSE reconnection
 */

interface ToastEvent {
  type: 'position_opened' | 'position_closed' | 'trade_closed' | 'position_pnl_change' | 'position_modified' | 'session_gap_detected';
  symbol: string;
  account: string;
  timestamp: number;
  details?: {
    pnl?: number;
    tradeId?: string;
    side?: string;
    qtyChange?: number;
  };
}

class ToastDeduplicationService {
  private recentEvents: Map<string, ToastEvent> = new Map();
  private readonly DEDUPLICATION_WINDOW = 60000; // 60 seconds to handle comprehensive snapshots
  
  /**
   * Check if toast should be shown and register the event
   * Returns true if toast should be shown, false if it's a duplicate
   */
  shouldShowToast(event: ToastEvent): boolean {
    const eventKey = this.generateEventKey(event);
    const now = Date.now();
    
    // Clean up old events first
    this.cleanupOldEvents(now);
    
    const existingEvent = this.recentEvents.get(eventKey);
    
    if (existingEvent) {
      const timeDiff = now - existingEvent.timestamp;
      
      if (timeDiff < this.DEDUPLICATION_WINDOW) {
        console.log(`🔄 Duplicate toast prevented: ${event.type} for ${event.symbol}/${event.account} (${timeDiff}ms ago)`);
        return false; // Duplicate within window
      }
    }
    
    // Register this event
    this.recentEvents.set(eventKey, { ...event, timestamp: now });
    
    return true; // Show the toast
  }
  
  /**
   * Generate unique key for position/trade identification
   * Handles the position_closed → trade_closed overlap scenario
   */
  private generateEventKey(event: ToastEvent): string {
    const baseKey = `${event.symbol}:${event.account}`;
    
    // Special handling for position closure / trade closure overlap
    if (event.type === 'position_closed' || event.type === 'trade_closed') {
      return `${baseKey}:closure`; // Same key for both types of closure events
    }
    
    return `${baseKey}:${event.type}`;
  }
  
  /**
   * Clean up events older than deduplication window
   */
  private cleanupOldEvents(currentTime: number): void {
    for (const [key, event] of this.recentEvents.entries()) {
      if (currentTime - event.timestamp > this.DEDUPLICATION_WINDOW) {
        this.recentEvents.delete(key);
      }
    }
  }
  
  /**
   * Force register an event (for comprehensive snapshots that should take precedence)
   */
  forceRegisterEvent(event: ToastEvent): void {
    const eventKey = this.generateEventKey(event);
    this.recentEvents.set(eventKey, { ...event, timestamp: Date.now() });
  }
  
  /**
   * Get stats for debugging
   */
  getStats(): { activeEvents: number; events: Array<{key: string; type: string; age: number}> } {
    const now = Date.now();
    const events = Array.from(this.recentEvents.entries()).map(([key, event]) => ({
      key,
      type: event.type,
      age: now - event.timestamp
    }));
    
    return {
      activeEvents: this.recentEvents.size,
      events
    };
  }
}

// Global instance
export const toastDeduplicationService = new ToastDeduplicationService();

// Export types for use in components
export type { ToastEvent };